/**
 * Albums (Oct 2026, design_handoff_messages Part A): the pure half.
 *
 * Up to ten photos and clips picked together send as ONE message. This file holds the
 * numbers and the rules — what a pick keeps, how the grid lays out, what the status line
 * says — and nothing that touches the DOM. The composer, the outbox, the bubble and the
 * viewer read it; the tests read all of it.
 */
import { formatBytes } from './messagesModel';
import { MAX_VIDEO_BYTES, MAX_MESSAGE_CLIP_MS as MAX_CLIP_MS } from '../../services/messageService';

export const MAX_ALBUM_ITEMS = 10;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Uploads in flight at once; the rest wait their turn in album order. */
export const UPLOAD_CONCURRENCY = 3;
/** The cover's shape. Portrait by default (1/1 and 4/3 were explored as tweaks). */
export const COVER_RATIO = '4/5';
/** A photo holds this long in the viewer; a clip plays its (trimmed) length. */
export const STILL_MS = 8000;

export const fmtDur = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Sort a pick into what is kept and what is skipped. `room` is how many more the tray takes.
 * Returns { kept: File[], skipped: number, overflow: number } plus the toasts to show, in order.
 * Nothing is rejected for length: a clip over 15 min is kept with a default window of its first
 * 15 min (the trim sheet is one tap away on its tile), so a multi-pick never stops to ask.
 */
export function sortPick(files, room) {
  const ok = [];
  let skipped = 0;
  for (const f of files || []) {
    const vid = (f.type || '').startsWith('video/');
    const img = (f.type || '').startsWith('image/');
    if (!vid && !img) { skipped += 1; continue; }
    if ((img && f.size > MAX_IMAGE_BYTES) || (vid && f.size > MAX_VIDEO_BYTES)) { skipped += 1; continue; }
    ok.push(f);
  }
  const toasts = [];
  if (skipped) toasts.push(`${skipped} ${skipped > 1 ? 'FILES' : 'FILE'} SKIPPED · PHOTOS 10 MB, VIDEOS 2 GB`);
  let kept = ok;
  let overflow = 0;
  if (room <= 0 && ok.length) {
    kept = [];
    overflow = ok.length;
    toasts.push(`${MAX_ALBUM_ITEMS} AT A TIME · SEND THESE FIRST`);
  } else if (ok.length > room) {
    kept = ok.slice(0, room);
    overflow = ok.length - room;
    toasts.push(`${MAX_ALBUM_ITEMS} AT A TIME · THE FIRST ${room} ARE IN`);
  }
  return { kept, skipped, overflow, toasts };
}

/** The default trim window for a clip: the whole thing, or its first 15 min (`MAX_MESSAGE_CLIP_MS`). */
export function defaultTrim(durationMs) {
  if (!durationMs) return null;
  return { startMs: 0, endMs: Math.min(durationMs, MAX_CLIP_MS) };
}

/** A clip's window in seconds (what will actually send). */
export function clipSec(item) {
  if (!item) return 0;
  // A tray item carries `trim`; a received item carries the DTO's `trimStartMs / trimEndMs`.
  if (item.trim && item.trim.endMs > item.trim.startMs) return (item.trim.endMs - item.trim.startMs) / 1000;
  if (item.trimEndMs > item.trimStartMs) return (item.trimEndMs - (item.trimStartMs || 0)) / 1000;
  return item.durationSec || 0;
}

/** `0:30 / 0:48` for a clip that will be cut, `0:12` for one that won't, '' for a photo. */
export function tileDuration(item) {
  if (item.kind !== 'video') return '';
  const full = item.durationSec || 0;
  if (!full) return '';
  const win = clipSec(item);
  return full * 1000 > MAX_CLIP_MS + 50 ? `${fmtDur(win)} / ${fmtDur(full)}` : fmtDur(full);
}

export const needsTrim = (item) => item.kind === 'video' && (item.durationSec || 0) * 1000 > MAX_CLIP_MS + 50;

/** `3 PHOTOS · 2 VIDEOS · 142.2 MB` */
export function trayMeta(items) {
  const photos = items.filter((p) => p.kind === 'image').length;
  const vids = items.length - photos;
  const parts = [];
  if (photos) parts.push(`${photos} ${photos > 1 ? 'PHOTOS' : 'PHOTO'}`);
  if (vids) parts.push(`${vids} ${vids > 1 ? 'VIDEOS' : 'VIDEO'}`);
  const bytes = items.reduce((a, p) => a + (p.size || 0), 0);
  if (bytes) parts.push(formatBytes(bytes));
  return parts.join(' · ');
}

/**
 * The grid: cover, then a row of up to 3, then a row of up to 2. A row's tiles share its width;
 * a row with one tile goes big (the cover's shape). Past 6 items the last tile carries +N,
 * where N = items − 5 (ten → +5).
 *
 * Returns [{ index, span, ratio, more }] over the twelve-column grid; `more` is the +N count
 * on the last tile, 0 otherwise. `index` is the item's position in the album.
 */
export function albumLayout(n, coverRatio = COVER_RATIO) {
  if (!n) return [];
  const tiles = [{ index: 0, span: 12, ratio: coverRatio, more: 0 }];
  const rest = Math.min(n - 1, 5);
  const row1 = Math.min(rest, 3);
  const row2 = rest - row1;
  for (let k = 0; k < rest; k += 1) {
    const len = k < 3 ? row1 : row2;
    const isLast = k === rest - 1;
    tiles.push({ index: k + 1, span: 12 / len, ratio: len === 1 ? coverRatio : '1/1', more: isLast && n > 6 ? n - 5 : 0 });
  }
  return tiles;
}

/**
 * The line under a sending album, from its items' states
 * ({ state: queued|uploading|processing|ready|failed, progress }).
 *   SENDING · 2 OF 5 · 48%   — anything still queued or uploading
 *   1 OF 5 DIDN’T SEND        — else anything failed (RETRY / SEND WITHOUT IT)
 *   PROCESSING VIDEO…         — else a clip still encoding
 *   null                      — all ready
 */
export function albumStatus(items) {
  if (!items?.length) return null;
  const n = items.length;
  const done = items.filter((i) => i.state === 'ready' || i.state === 'processing').length;
  const sending = items.some((i) => i.state === 'queued' || i.state === 'uploading');
  const failed = items.filter((i) => i.state === 'failed').length;
  const processing = items.some((i) => i.state === 'processing');
  if (sending) {
    const pct = Math.round(items.reduce((a, i) => a + (i.state === 'ready' || i.state === 'processing' ? 100 : i.state === 'failed' ? 0 : (i.progress || 0)), 0) / n);
    return { kind: 'sending', text: `SENDING · ${done} OF ${n} · ${pct}%`, spin: true, danger: false };
  }
  if (failed) return { kind: 'failed', text: `${failed} OF ${n} DIDN’T SEND`, spin: false, danger: true };
  if (processing) return { kind: 'processing', text: 'PROCESSING VIDEO…', spin: true, danger: false };
  return null;
}

/** The inbox / quote line for an album. */
export const albumPreview = (count) => `[ ALBUM · ${count || 0} ]`;

/** `PHOTO 3 OF 5` / `VIDEO 2 OF 5 · 0:09` */
export function viewerPosition(item, index, total) {
  if (!item) return '';
  const what = item.kind === 'video' ? 'VIDEO' : 'PHOTO';
  const dur = item.kind === 'video' && item.durationSec ? ` · ${fmtDur(clipSec(item))}` : '';
  return `${what} ${index + 1} OF ${total}${dur}`;
}

/** How long the viewer holds an item: a photo STILL_MS, a clip its window (≤ 15 min). */
export function viewerDurationMs(item) {
  if (!item) return STILL_MS;
  if (item.kind !== 'video') return STILL_MS;
  const sec = clipSec(item) || item.durationSec || 10;
  return Math.max(1, Math.min(sec, MAX_CLIP_MS / 1000)) * 1000;
}
